import { Data, Effect } from 'effect';

export type CatalogOperation =
  | 'descriptor_directory'
  | 'descriptor_io'
  | 'descriptor_normalize'
  | 'jsonc_parse';

/**
 * Redacted failure for catalog descriptor programs. Descriptor contents and
 * host filesystem causes stay outside the serializable tagged value.
 */
export class CatalogRuntimeError extends Data.TaggedError('CatalogRuntimeError')<{
  readonly operation: CatalogOperation;
  readonly subject: string;
}> {}

const catalogRuntimeCauses = new WeakMap<CatalogRuntimeError, unknown>();

function catalogRuntimeError(
  operation: CatalogOperation,
  subject: string,
  cause: unknown,
): CatalogRuntimeError {
  const error = new CatalogRuntimeError({ operation, subject });
  catalogRuntimeCauses.set(error, cause);
  return error;
}

export function attemptCatalog<A>(
  operation: CatalogOperation,
  subject: string,
  evaluate: () => A,
): Effect.Effect<A, CatalogRuntimeError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => catalogRuntimeError(operation, subject, cause),
  });
}

export function restoreCatalogRuntimeError<A>(
  program: Effect.Effect<A, CatalogRuntimeError>,
): Effect.Effect<A, unknown> {
  return program.pipe(
    Effect.catch((error) => Effect.fail(catalogRuntimeCauses.get(error) ?? error)),
  );
}

export function runCatalogSync<A>(program: Effect.Effect<A, CatalogRuntimeError>): A {
  return Effect.runSync(restoreCatalogRuntimeError(program));
}
